import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import appFeatures from "@/appFeatures";
import { COLOR_KEYS, COLORS } from "@/constants/colors";
import type {
  Dataset,
  DatasetData,
  DatasetExploreItem,
  DatasetFilter,
  DatasetHistory,
  DatasetIndices,
  DatasetsMap,
  EColorLabel,
  EColorNames,
  H3Configuration,
  WeightValueEntry,
  WeightValues
} from "@/types/dataset";
import {
  performRecalculateAllPercentages,
  performRecalculateAllPercentagesV2
} from "@/utils/weightValues";

export interface DatasetState {
  /** Store object that keeps all the datasets being used for analysis which includes both the available and deleted datasets */
  datasets: DatasetsMap;
  filters: {
    [datasetId: string]: DatasetFilter[];
  };
  rawDatasets: {
    [id: string]: DatasetData;
  };
  visualizationOrder: string[];
  selectedDataset: Dataset | null;
  // TODO: To handle administrative data zoom level
  administrativeData: string[];
  datasetIndices: DatasetIndices;
  weightValues: WeightValues; // Weight values for spatial analysis
  history: DatasetHistory;
  analysisId: string | null;
  selectedDatasetExplorer: DatasetExploreItem | null;
}

export const datasetInitialState: DatasetState = {
  datasets: {},
  filters: {},
  visualizationOrder: [],
  rawDatasets: {},
  selectedDataset: null,
  administrativeData: [],
  datasetIndices: {},
  weightValues: {},
  history: {
    datasets: {},
    visualizationOrder: [],
    rawDatasets: {}
  },
  analysisId: null,
  selectedDatasetExplorer: null
};

export const datasetSlice = createSlice({
  name: "dataset",
  initialState: datasetInitialState,
  reducers: {
    addRawDataset: (
      state,
      action: PayloadAction<{ dataset: DatasetData; id: string }>
    ) => {
      const { dataset, id } = action.payload;

      state.rawDatasets[id] = {
        ...dataset,
        display_name: dataset.display_name ?? dataset?.name // TODO: remove when BE has returned display_name
      };
    },
    updateRawDatasetDisplayName: (
      state,
      action: PayloadAction<{ datasetId: string; displayName: string }>
    ) => {
      const { datasetId, displayName } = action.payload;
      if (state.rawDatasets[datasetId]) {
        state.rawDatasets[datasetId].display_name = displayName;
      }
    },
    removeRawDataset: (state, action: PayloadAction<string>) => {
      const datasetId = action.payload;
      delete state.rawDatasets[datasetId];
    },
    clearRawDatasets: (state) => {
      state.rawDatasets = {};
    },
    setAnalysisId: (state, action: PayloadAction<string | null>) => {
      state.analysisId = action.payload;
    },
    addDataset: (state, action: PayloadAction<Dataset>) => {
      const dataset = action.payload;

      state.datasets[dataset.data.id] = {
        ...dataset,
        data: {
          ...dataset.data,
          display_name: dataset.data.display_name ?? dataset.data.name // TODO: remove when BE has returned display_name
        }
      };

      // Ensure no duplicates in visualizationOrder
      if (!state.visualizationOrder.includes(dataset.data.id)) {
        state.visualizationOrder = Array.isArray(state.visualizationOrder)
          ? [dataset.data.id, ...state.visualizationOrder]
          : [dataset.data.id];
      }
    },
    updateDatasetDisplayName: (
      state,
      action: PayloadAction<{
        datasetId: string;
        displayName: string;
      }>
    ) => {
      const { datasetId, displayName } = action.payload;
      if (state.datasets[datasetId]) {
        state.datasets[datasetId].data.display_name = displayName;
      }
    },
    updateDatasetH3Config: (
      state,
      action: PayloadAction<{
        datasetId: string;
        config: H3Configuration | null;
      }>
    ) => {
      const { datasetId, config } = action.payload;
      state.datasets[datasetId].h3Configuration = config;
    },
    updateDatasetStyle: (
      state,
      action: PayloadAction<{
        datasetId: string;
        style: Dataset["style"];
      }>
    ) => {
      const { datasetId, style } = action.payload;

      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style = style;
      }
    },

    updateDatasetStyleVisibleLabel: (
      state,
      action: PayloadAction<{
        datasetId: string;
        visible: boolean;
      }>
    ) => {
      const { datasetId, visible } = action.payload;

      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style.labelConfig.show_label = visible;
      }
    },

    updateDatasetStyleAttributeLabel: (
      state,
      action: PayloadAction<{
        datasetId: string;
        attribute_name: string;
        attribute_id: string;
      }>
    ) => {
      const { datasetId, attribute_id, attribute_name } = action.payload;

      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style.labelConfig.attribute_id = attribute_id;
        state.datasets[datasetId].style.labelConfig.attribute_name =
          attribute_name;
      }
    },

    updateDatasetStyleColorLabel: (
      state,
      action: PayloadAction<{
        datasetId: string;
        color: EColorLabel;
      }>
    ) => {
      const { datasetId, color } = action.payload;

      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style.labelConfig.color_scale = color;
      }
    },

    updateDatasetStyleOpacityLabel: (
      state,
      action: PayloadAction<{
        datasetId: string;
        opacity: number;
      }>
    ) => {
      const { datasetId, opacity } = action.payload;

      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style.labelConfig.opacity = opacity;
      }
    },

    updateDatasetColorStyle: (
      state,
      action: PayloadAction<{
        datasetId: string;
        colorFamily: EColorNames;
      }>
    ) => {
      const { datasetId, colorFamily } = action.payload;
      if (state.datasets[datasetId]) {
        const currentStyle = state.datasets[datasetId].style;

        const colorRanges = currentStyle.colorRanges?.map((range, index) => ({
          ...range,
          color: String(COLORS[colorFamily][COLOR_KEYS[index]])
        }));

        state.datasets[datasetId].style = {
          ...currentStyle,
          colorFamily,
          colorRanges
        };
      }
    },

    updateDatasetStyleEnabledRank: (
      state,
      action: PayloadAction<{
        datasetId: string;
        isEnabled: boolean;
      }>
    ) => {
      const { datasetId, isEnabled } = action.payload;

      if (state.datasets[datasetId]?.style?.rankConfig) {
        state.datasets[datasetId].style.rankConfig.isEnabled = isEnabled;
      }
    },

    updateDatasetStyleMaxRanking: (
      state,
      action: PayloadAction<{
        datasetId: string;
        maxRanking: number;
      }>
    ) => {
      const { datasetId, maxRanking } = action.payload;

      if (state.datasets[datasetId]?.style?.rankConfig) {
        state.datasets[datasetId].style.rankConfig.maxRanking = maxRanking;
      }
    },

    addAdministrativeData: (state, action: PayloadAction<string>) => {
      const datasetId = action.payload;

      state.administrativeData = Array.isArray(state.administrativeData)
        ? [...state.administrativeData, datasetId]
        : [datasetId];
    },
    setSelectedDataset: (state, action: PayloadAction<Dataset>) => {
      state.selectedDataset = action.payload;
    },
    clearSelectedDataset: (state) => {
      state.selectedDataset = null;
    },
    setDatasetIsFetching: (
      state,
      action: PayloadAction<{ datasetId: string; isFetching: boolean }>
    ) => {
      const { datasetId, isFetching } = action.payload;
      state.datasets[datasetId].properties.isFetching = isFetching;
    },
    updateDatasetPublishedStatus: (
      state,
      action: PayloadAction<{ datasetId: string; published: boolean }>
    ) => {
      const { datasetId, published } = action.payload;
      if (state.datasets[datasetId]) {
        state.datasets[datasetId].data.published = published;
        state.datasets[datasetId].data.available = published;
      }
    },
    removeDataset: (state, action: PayloadAction<string>) => {
      const datasetId = action.payload;
      const [baseId, indexStr] = datasetId.split("_");
      const index = parseInt(indexStr);

      delete state.datasets[datasetId];
      delete state.filters[datasetId];
      delete state.weightValues[datasetId]; // Also remove weight values

      state.visualizationOrder = state.visualizationOrder.filter(
        (id) => id !== datasetId
      );

      state.administrativeData = state.administrativeData.filter(
        (id) => id !== datasetId
      );

      if (state.weightValues[datasetId]) {
        delete state.weightValues[datasetId];
      }

      // Update indices tracking
      if (state.datasetIndices[baseId]) {
        state.datasetIndices[baseId].usedIndices = state.datasetIndices[
          baseId
        ].usedIndices.filter((i) => i !== index);

        // If no more datasets with this base ID exist, clean up
        if (state.datasetIndices[baseId].usedIndices.length === 0) {
          delete state.datasetIndices[baseId];
        }
      }
    },
    clearDataset: (state) => {
      state.datasets = {};
      state.visualizationOrder = [];
      state.rawDatasets = {};
    },
    setDatasetHistory: (
      state,
      action: PayloadAction<{
        datasets: {
          [id: string]: Dataset;
        };
        visualizationOrder: string[];
        rawDatasets: {
          [id: string]: DatasetData;
        };
      }>
    ) => {
      state.history = action.payload;
    },
    addFilter: (
      state,
      action: PayloadAction<{
        datasetId: string;
        filter: DatasetFilter[];
      }>
    ) => {
      const { datasetId, filter } = action.payload;

      if (!state.filters[datasetId]) state.filters[datasetId] = [];

      state.filters[datasetId] = filter;
    },
    removeFilter: (
      state,
      action: PayloadAction<{ datasetId: string; filterId: string }>
    ) => {
      const { datasetId, filterId } = action.payload;
      if (state.filters[datasetId]) {
        state.filters[datasetId] = state.filters[datasetId].filter(
          (f) => f.filterId !== filterId
        );
      }
    },
    updateFilters: (
      state,
      action: PayloadAction<{
        datasetId: string;
        filters: DatasetFilter[];
      }>
    ) => {
      const { datasetId, filters } = action.payload;
      state.filters[datasetId] = filters;
    },
    clearFilters: (state, action: PayloadAction<string>) => {
      const datasetId = action.payload;
      if (state.filters[datasetId]) {
        state.filters[datasetId] = [];
      }
    },
    clearAllFilters: (state) => {
      state.filters = {};
    },
    reorderDatasets: (state, action: PayloadAction<string[]>) => {
      state.visualizationOrder = action.payload;
    },

    toggleDatasetVisibility: (
      state,
      action: PayloadAction<{ datasetId: string; visibility?: boolean }>
    ) => {
      const { datasetId, visibility } = action.payload;
      const dataset = state.datasets[datasetId];
      if (dataset) {
        const newVisibility = visibility ?? !dataset.properties.isVisible;

        dataset.properties.isVisible = newVisibility;
        dataset.style.visibility = newVisibility ? "visible" : "none";
      }
    },
    updateDatasetSelectedCategory: (
      state,
      action: PayloadAction<{
        datasetId: string;
        selectedCategoryName: string;
      }>
    ) => {
      const { datasetId, selectedCategoryName } = action.payload;
      const selectedCategory = state.datasets[datasetId].categories.find(
        (category) => category.attribute_name === selectedCategoryName
      );

      if (state.datasets[datasetId] && selectedCategory) {
        state.datasets[datasetId].selectedCategory = selectedCategory;
      }
    },
    resetDatasetToHistoryDataset: (state) => {
      state.datasets = state.history.datasets;
      state.visualizationOrder = state.history.visualizationOrder;
      state.rawDatasets = state.history.rawDatasets;

      state.history = {
        datasets: {},
        visualizationOrder: [],
        rawDatasets: {}
      };
    },
    hideAllDatasetVisibility: (state) => {
      Object.keys(state.datasets).forEach((key) => {
        state.datasets[key].properties.isVisible = false;
      });
    },
    incrementDatasetCount: (
      state,
      action: PayloadAction<{ baseId: string; usedIndex: number }>
    ) => {
      const { baseId, usedIndex } = action.payload;

      state.datasetIndices = {
        ...state.datasetIndices,
        [baseId]: {
          counter: (state.datasetIndices[baseId]?.counter || 0) + 1,
          usedIndices: [
            ...(state.datasetIndices[baseId]?.usedIndices || []),
            usedIndex
          ]
        }
      };
    },

    updateDatasetOpacity: (
      state,
      action: PayloadAction<{ datasetId: string; opacity: number }>
    ) => {
      const { datasetId, opacity } = action.payload;
      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style.fillOpacity = opacity;
      }
    },
    updateDatasetStrokeWidth: (
      state,
      action: PayloadAction<{ datasetId: string; width: number }>
    ) => {
      const { datasetId, width } = action.payload;
      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style.stroke.width = width;
      }
    },
    updateDatasetStrokeAttribute: (
      state,
      action: PayloadAction<{
        datasetId: string;
        attributeId: string;
        attributeName: string;
      }>
    ) => {
      const { datasetId, attributeId, attributeName } = action.payload;
      if (state.datasets[datasetId]) {
        state.datasets[datasetId].style.stroke.attribute_id = attributeId;
        state.datasets[datasetId].style.stroke.attribute_name = attributeName;
      }
    },
    cleanupVisualizationOrder: (state) => {
      // Remove any IDs from visualizationOrder that don't exist in datasets
      state.visualizationOrder = state.visualizationOrder.filter(
        (id) => id in state.datasets
      );

      // Add any datasets that are missing from visualizationOrder
      const missingIds = Object.keys(state.datasets).filter(
        (id) => !state.visualizationOrder.includes(id)
      );

      if (missingIds.length > 0) {
        state.visualizationOrder = [...state.visualizationOrder, ...missingIds];
      }
    },
    setWeightValuesForDatasetId: (
      state,
      action: PayloadAction<{
        datasetId: string;
        data: { [categoryIdx: string]: WeightValueEntry };
      }>
    ) => {
      const { datasetId, data } = action.payload;

      state.weightValues[datasetId] = {
        weight: Object.keys(data).length.toString(),
        attributes: data
      };
    },
    /** Action to assign initial weight values for dataset attributes that are eligible to be assigned a weight value */
    initializeWeightValues: (state) => {
      if (appFeatures.isNewSetupWeightOptionsEnabled) {
        performRecalculateAllPercentagesV2(state);
      } else {
        performRecalculateAllPercentages(state);
      }
    },
    saveWeightValues: (
      state,
      action: PayloadAction<{
        weightValues: WeightValues;
      }>
    ) => {
      // Save all weight values at once
      state.weightValues = action.payload.weightValues;
    },
    setDatasetState: (_, action: PayloadAction<DatasetState>) => {
      return action.payload;
    },
    setSelectedDatasetExplorer: (
      state,
      action: PayloadAction<DatasetExploreItem | null>
    ) => {
      state.selectedDatasetExplorer = action.payload;
    },
    resetDatasetState: () => datasetInitialState
  }
});

export const {
  addDataset,
  addRawDataset,
  removeRawDataset,
  clearRawDatasets,
  updateDatasetStyle,
  updateDatasetColorStyle,
  updateDatasetSelectedCategory,
  updateRawDatasetDisplayName,
  updateDatasetDisplayName,
  updateDatasetStyleVisibleLabel,
  updateDatasetStyleAttributeLabel,
  updateDatasetStyleColorLabel,
  updateDatasetStyleOpacityLabel,
  removeDataset,
  setSelectedDataset,
  clearSelectedDataset,
  addFilter,
  clearDataset,
  removeFilter,
  updateFilters,
  clearAllFilters,
  clearFilters,
  reorderDatasets,
  toggleDatasetVisibility,
  incrementDatasetCount,
  cleanupVisualizationOrder,
  addAdministrativeData,
  setDatasetState,
  setAnalysisId,
  setDatasetIsFetching,
  resetDatasetState,
  updateDatasetOpacity,
  setDatasetHistory,
  resetDatasetToHistoryDataset,
  hideAllDatasetVisibility,
  initializeWeightValues,
  saveWeightValues,
  setWeightValuesForDatasetId,
  updateDatasetStrokeWidth,
  updateDatasetStrokeAttribute,
  updateDatasetPublishedStatus,
  updateDatasetH3Config,
  setSelectedDatasetExplorer,
  updateDatasetStyleMaxRanking,
  updateDatasetStyleEnabledRank
} = datasetSlice.actions;

export const datasetReducer = datasetSlice.reducer;
